// Typed API client for the itms-qc API. Attaches the MSAL access token to
// every request and surfaces the parsed JSON body. Throws ApiError on
// non-2xx with the server's `error` field for upstream display.
//
// Request bodies / query params are snake_case (the API's input contract);
// responses are camelCase DTOs from @itms-qc/shared.

import type {
  QcAuditRun,
  QcAuditRunDetail,
  QcAuditTemplate,
  QcCase,
  QcCaseDetail,
  QcCaseListResponse,
  QcCaseNote,
  QcCasePriority,
  QcCaseStatus,
  QcCaseType,
  QcDashboard,
  QcDuplicateSuggestion,
  QcEnvironment,
  QcFeatureIntakeResponse,
  QcIncident,
  QcIntakeMessage,
  QcMe,
  QcModule,
  QcModuleSuggestion,
  QcPeopleMap,
  QcSubmodule,
} from '@itms-qc/shared';

// Base for all API calls. The pipeline bakes VITE_API_BASE_URL (including the
// /api suffix) into the build so the portal calls the API service directly;
// the '/api' fallback only applies to local dev behind the Vite proxy.
export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  token: string,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text) as unknown;
    } catch {
      // Body wasn't JSON — typically an HTML error page (App Service cold-start
      // 502/503, gateway 404, an auth redirect) or the SPA's own index.html when
      // VITE_API_BASE_URL is misconfigured. Surface a clean ApiError instead of
      // letting a raw "Unexpected token … is not valid JSON" SyntaxError escape.
      throw new ApiError(
        res.status,
        res.ok
          ? `Expected JSON from ${path} but got a non-JSON response (HTTP ${res.status})`
          : `HTTP ${res.status}`,
        text,
      );
    }
  }
  if (!res.ok) {
    const message =
      parsed && typeof parsed === 'object' && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, message, parsed);
  }
  return parsed as T;
}

export interface ListCasesParams {
  moduleId?: number;
  submoduleId?: number;
  type?: QcCaseType;
  status?: QcCaseStatus;
  priority?: QcCasePriority;
  filedByMe?: boolean;
  search?: string;
  page?: number;
  pageSize?: number;
  includeArchived?: boolean;
}

export interface CreateCaseInput {
  title: string;
  description: string;
  type: QcCaseType;
  moduleId: number;
  submoduleId?: number | null;
  priority?: QcCasePriority;
  environment?: QcEnvironment | null;
  /** Blob URLs of the first incident's attachments (uploaded via uploadAttachments). */
  attachmentUrls?: string[];
  /** Where the report came from: 'manual' (default) or a deep link from another app. */
  source?: string;
  /** The module the user filed FROM when arriving by deep link (?module=<wise-iam id>). */
  sourceModuleSlug?: string | null;
}

export interface UpdateCasePatch {
  title?: string;
  description?: string;
  type?: QcCaseType;
  status?: QcCaseStatus;
  priority?: QcCasePriority;
  moduleId?: number;
  submoduleId?: number | null;
  assignedTo?: string | null;
}

export interface AttachIncidentInput {
  description: string;
  environment?: QcEnvironment | null;
  attachmentUrls?: string[];
}

export const api = {
  me: (token: string) => request<QcMe>(token, 'GET', '/me'),

  dashboard: (token: string) => request<QcDashboard>(token, 'GET', '/qc/dashboard'),

  // === Cases ===
  listCases: (token: string, params: ListCasesParams = {}) => {
    const qs = new URLSearchParams();
    if (params.moduleId !== undefined) qs.set('module_id', String(params.moduleId));
    if (params.submoduleId !== undefined) qs.set('submodule_id', String(params.submoduleId));
    if (params.type) qs.set('type', params.type);
    if (params.status) qs.set('status', params.status);
    if (params.priority) qs.set('priority', params.priority);
    if (params.filedByMe) qs.set('filed_by_me', 'true');
    if (params.search) qs.set('search', params.search);
    if (params.page !== undefined) qs.set('page', String(params.page));
    if (params.pageSize !== undefined) qs.set('page_size', String(params.pageSize));
    if (params.includeArchived) qs.set('include_archived', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<QcCaseListResponse>(token, 'GET', `/qc/cases${suffix}`);
  },

  getCase: (token: string, id: number) =>
    request<{ case: QcCaseDetail }>(token, 'GET', `/qc/cases/${id}`),

  createCase: (token: string, input: CreateCaseInput) =>
    request<{ case: QcCase }>(token, 'POST', '/qc/cases', {
      title: input.title,
      description: input.description,
      type: input.type,
      module_id: input.moduleId,
      submodule_id: input.submoduleId ?? null,
      priority: input.priority,
      environment: input.environment ?? null,
      attachment_urls: input.attachmentUrls ?? [],
      ...(input.source ? { source: input.source } : {}),
      ...(input.sourceModuleSlug ? { source_module_slug: input.sourceModuleSlug } : {}),
    }),

  updateCase: (token: string, id: number, patch: UpdateCasePatch) => {
    const body: Record<string, unknown> = {};
    if (patch.title !== undefined) body.title = patch.title;
    if (patch.description !== undefined) body.description = patch.description;
    if (patch.type !== undefined) body.type = patch.type;
    if (patch.status !== undefined) body.status = patch.status;
    if (patch.priority !== undefined) body.priority = patch.priority;
    if (patch.moduleId !== undefined) body.module_id = patch.moduleId;
    if (patch.submoduleId !== undefined) body.submodule_id = patch.submoduleId;
    if (patch.assignedTo !== undefined) body.assigned_to = patch.assignedTo;
    return request<{ case: QcCaseDetail }>(token, 'PATCH', `/qc/cases/${id}`, body);
  },

  addNote: (
    token: string,
    caseId: number,
    body: string,
    attachmentUrls: string[] = [],
    visibility: 'public' | 'internal' = 'public',
  ) =>
    request<{ note: QcCaseNote }>(token, 'POST', `/qc/cases/${caseId}/notes`, {
      body,
      attachment_urls: attachmentUrls,
      visibility,
    }),

  mergeCase: (token: string, caseId: number, targetCaseId: number) =>
    request<{ case: QcCaseDetail; movedIncidents: number }>(
      token,
      'POST',
      `/qc/cases/${caseId}/merge`,
      { target_case_id: targetCaseId },
    ),

  reopenCase: (token: string, caseId: number) =>
    request<{ case: QcCaseDetail }>(token, 'POST', `/qc/cases/${caseId}/reopen`),

  // Reporter one-click verification of a 'fixed' case: works → closed,
  // still broken → back to in_progress. Reporter-scoped server-side.
  verifyCase: (token: string, caseId: number, works: boolean, comment?: string) =>
    request<{ case: QcCaseDetail }>(token, 'POST', `/qc/cases/${caseId}/verify`, {
      works,
      comment: comment?.trim() || undefined,
    }),

  // Accept/decline decision on a feature request (qc.manage): accept →
  // 'accepted', decline → 'wont_do'. One note + status + reporter email.
  // internalNote (optional) is stored as an INTERNAL dev-only note — build
  // direction the reporter never sees.
  decideCase: (token: string, caseId: number, accept: boolean, comment?: string, internalNote?: string) =>
    request<{ case: QcCaseDetail }>(token, 'POST', `/qc/cases/${caseId}/decision`, {
      accept,
      comment: comment?.trim() || undefined,
      internal_note: internalNote?.trim() || undefined,
    }),

  listIncidents: (token: string, caseId: number) =>
    request<{ incidents: QcIncident[] }>(token, 'GET', `/qc/cases/${caseId}/incidents`),

  // === Incidents ===
  // case_id set → attach to an existing case (auto-reopens archived cases).
  attachIncident: (token: string, caseId: number, input: AttachIncidentInput) =>
    request<{ incident: QcIncident; reopened: boolean }>(token, 'POST', '/qc/incidents', {
      case_id: caseId,
      description: input.description,
      environment: input.environment ?? null,
      source: 'manual',
      attachment_urls: input.attachmentUrls ?? [],
    }),

  // === Modules ===
  listModules: (token: string, includeInactive = false) =>
    request<{ modules: QcModule[] }>(
      token,
      'GET',
      `/qc/modules${includeInactive ? '?include_inactive=true' : ''}`,
    ),

  createModule: (
    token: string,
    input: { slug: string; displayName: string; suite: string; iamModuleId?: string | null },
  ) =>
    request<{ module: QcModule }>(token, 'POST', '/qc/modules', {
      slug: input.slug,
      display_name: input.displayName,
      suite: input.suite,
      iam_module_id: input.iamModuleId ?? null,
    }),

  updateModule: (
    token: string,
    id: number,
    patch: { displayName?: string; suite?: string; active?: boolean; iamModuleId?: string | null },
  ) => {
    const body: Record<string, unknown> = {};
    if (patch.displayName !== undefined) body.display_name = patch.displayName;
    if (patch.suite !== undefined) body.suite = patch.suite;
    if (patch.active !== undefined) body.active = patch.active;
    if (patch.iamModuleId !== undefined) body.iam_module_id = patch.iamModuleId;
    return request<{ module: QcModule }>(token, 'PATCH', `/qc/modules/${id}`, body);
  },

  createSubmodule: (
    token: string,
    moduleId: number,
    input: { slug: string; displayName: string },
  ) =>
    request<{ submodule: QcSubmodule }>(token, 'POST', `/qc/modules/${moduleId}/submodules`, {
      slug: input.slug,
      display_name: input.displayName,
    }),

  updateSubmodule: (
    token: string,
    moduleId: number,
    submoduleId: number,
    patch: { displayName?: string; active?: boolean },
  ) => {
    const body: Record<string, unknown> = {};
    if (patch.displayName !== undefined) body.display_name = patch.displayName;
    if (patch.active !== undefined) body.active = patch.active;
    return request<{ submodule: QcSubmodule }>(
      token,
      'PATCH',
      `/qc/modules/${moduleId}/submodules/${submoduleId}`,
      body,
    );
  },

  // === AI suggestions (both fail-safe: empty results on AI trouble) ===
  suggestDuplicates: (
    token: string,
    input: { title: string; description: string; moduleId: number },
  ) =>
    request<{ suggestions: QcDuplicateSuggestion[] }>(token, 'POST', '/qc/suggest/duplicates', {
      title: input.title,
      description: input.description,
      module_id: input.moduleId,
    }),

  suggestModule: (token: string, input: { title: string; description: string }) =>
    request<{ suggestion: QcModuleSuggestion | null }>(token, 'POST', '/qc/suggest/module', {
      title: input.title,
      description: input.description,
    }),

  // Guided feature-request intake — one turn at a time (next question, final
  // synthesis, or 'disabled'). Fail-safe by contract; see suggest.ts.
  featureIntake: (
    token: string,
    input: { title: string; moduleName?: string | null; history: QcIntakeMessage[] },
  ) =>
    request<QcFeatureIntakeResponse>(token, 'POST', '/qc/suggest/feature-intake', {
      title: input.title,
      module_name: input.moduleName ?? null,
      history: input.history,
    }),

  // === Attachments (SAS flow; see api/src/routes/attachments.ts) ===
  attachmentUploadUrl: (
    token: string,
    input: { fileName: string; contentType: string; sizeBytes: number },
  ) =>
    request<{ blobPath: string; blobUrl: string; uploadUrl: string }>(
      token,
      'POST',
      '/qc/attachments/upload-url',
      input,
    ),

  attachmentDownloadUrl: (token: string, attachmentUrl: string) =>
    request<{ downloadUrl: string }>(token, 'POST', '/qc/attachments/download-url', {
      attachment_url: attachmentUrl,
    }),
};

/**
 * Full attachment upload helper: request a write SAS, PUT the file to blob
 * storage, return the bare blob URL to store on the incident
 * (qc_incidents.attachment_url). Mirrors the skeleton's three-step flow,
 * minus the finalize step (QC keeps no attachments table).
 */
// === Audits ===

export const auditApi = {
  listTemplates: (token: string) =>
    request<{ templates: QcAuditTemplate[] }>(token, 'GET', '/qc/audits/templates'),
  listRuns: (token: string, templateId?: number) =>
    request<{ runs: QcAuditRun[]; people: QcPeopleMap }>(
      token,
      'GET',
      `/qc/audits/runs${templateId !== undefined ? `?template_id=${templateId}` : ''}`,
    ),
  startRun: (token: string, templateId: number, targetEnvironment: string) =>
    request<QcAuditRunDetail>(token, 'POST', '/qc/audits/runs', { templateId, targetEnvironment }),
  getRun: (token: string, runId: number) =>
    request<QcAuditRunDetail>(token, 'GET', `/qc/audits/runs/${runId}`),
  setItemResult: (
    token: string,
    runId: number,
    runItemId: number,
    result: 'pass' | 'fail' | 'blocked' | null,
    note?: string,
  ) =>
    request<QcAuditRunDetail>(token, 'PATCH', `/qc/audits/runs/${runId}/items/${runItemId}`, {
      result,
      note,
    }),
  completeRun: (token: string, runId: number, abandoned = false) =>
    request<QcAuditRunDetail>(token, 'POST', `/qc/audits/runs/${runId}/complete`, { abandoned }),
};

export async function uploadAttachment(token: string, file: File): Promise<string> {
  const { blobUrl, uploadUrl } = await api.attachmentUploadUrl(token, {
    fileName: file.name,
    contentType: file.type,
    sizeBytes: file.size,
  });
  // x-ms-blob-type header is required for block-blob PUT against Azure Blob.
  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.type,
    },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`Blob upload failed (${putRes.status}): ${await putRes.text()}`);
  }
  return blobUrl;
}

/**
 * Upload several files and return their blob URLs in the same order. Uploads
 * run in parallel; if any fails the whole batch rejects (the caller surfaces
 * the error and nothing is submitted).
 */
export function uploadAttachments(token: string, files: File[]): Promise<string[]> {
  return Promise.all(files.map((f) => uploadAttachment(token, f)));
}
